// Theme: visual mood of a race (sky, light, fog, terrain colours, scenery, weather).
import type { CreationOf, ThemeContent } from '@gruvetto/contracts';
import { defineKind, warn } from '../kind.js';

const LAND: ThemeContent['land'] = {
  grass: '#567f33', grassLight: '#94b252', dry: '#b3a35a', dirt: '#8c7656', rock: '#7e786c', sand: '#c8b88c', snow: '#f1f4f8', water: '#1d5a6e', foliage: '#5c7f3a',
  fields: ['#6f9a35', '#87a83f', '#c9b356', '#d8c35a', '#8a6b45', '#5d8a2e', '#b8c24a', '#7aa84a', '#e2cf3a'], snowLine: 420,
};
const DECOR: ThemeContent['decor'] = { trees: 1, conifers: 0.3, poplars: 0.15, rocks: 1, grass: 1, flowers: 1, crowd: 1 };

/** Official moods: the three original ones (ids kept) and two seasons. */
export const OFFICIAL_THEMES: Record<string, { name: string; desc: string; level?: number; content: ThemeContent }> = {
  jour: {
    name: 'Broad daylight', desc: 'Bright sun, blue sky, green countryside.',
    content: { sky: { sunElevation: 48, sunAzimuth: 160, haze: 0.07, sun: '#fff1dc', sky: '#5d9be6', fog: '#b9d3ee', cloud: '#ffffff', fogNear: 600, fogFar: 6000, exposure: 0.52, sunIntensity: 3.4, ambient: 0.9, clouds: 0.8, tint: 0.55 }, land: LAND, decor: DECOR, weather: 'clair' },
  },
  golden: {
    name: 'Golden hour', desc: 'Low, warm sun, long shadows.',
    content: { sky: { sunElevation: 11, sunAzimuth: 235, haze: 0.55, sun: '#ffc07a', sky: '#ffd8b0', fog: '#e8c6a2', cloud: '#ffe0c0', fogNear: 600, fogFar: 6000, exposure: 0.55, sunIntensity: 3.6, ambient: 0.75, clouds: 1 }, land: LAND, decor: DECOR, weather: 'clair' },
  },
  coucher: {
    name: 'Sunset', desc: 'Orange and pink sky at the horizon.', level: 5,
    content: { sky: { sunElevation: 6, sunAzimuth: 255, haze: 0.9, sun: '#ff8a50', sky: '#ffa889', fog: '#d09a84', cloud: '#ffc9b4', fogNear: 500, fogFar: 5500, exposure: 0.62, sunIntensity: 3, ambient: 1.05, clouds: 1 }, land: LAND, decor: DECOR, weather: 'clair' },
  },
  automne: {
    name: 'Autumn forest', desc: 'Russet foliage, light mist and ploughed fields.',
    content: {
      sky: { sunElevation: 22, sunAzimuth: 200, haze: 0.45, sun: '#ffd9a0', sky: '#d9d2c4', fog: '#c9bba6', cloud: '#efe6da', fogNear: 160, fogFar: 1800, exposure: 0.5, sunIntensity: 2.6, ambient: 0.8, clouds: 1.6 },
      land: { ...LAND, grass: '#6b6a2e', grassLight: '#9c8a3c', dry: '#b07a3a', foliage: '#c4622a', fields: ['#8a6b45', '#a5763e', '#6f5a36', '#b5873f', '#7d6a2f'] },
      decor: { ...DECOR, trees: 1.6, conifers: 0.2, poplars: 0.3, flowers: 0.2 }, weather: 'brume',
    },
  },
  hiver: {
    name: 'Snowy pass', desc: 'Falling snow, fir trees and cold light.',
    content: {
      sky: { sunElevation: 18, sunAzimuth: 170, haze: 0.3, sun: '#e8f0ff', sky: '#c9d8ea', fog: '#d8e2ee', cloud: '#f4f7fb', fogNear: 200, fogFar: 2200, exposure: 0.66, sunIntensity: 2.6, ambient: 1.3, clouds: 1.5 },
      land: { ...LAND, grass: '#dfe6ee', grassLight: '#f4f7fb', dry: '#c9d2dc', dirt: '#8f8f93', foliage: '#2c5a3a', fields: ['#e8edf3', '#dde4ec', '#f1f4f8'], snowLine: 20 },
      decor: { ...DECOR, conifers: 0.9, poplars: 0, grass: 0.2, flowers: 0, crowd: 0.6 }, weather: 'neige',
    },
  },
};

const lum = (hex: string) => { const n = parseInt(hex.slice(1), 16); return (0.2126 * (n >> 16) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255; };

export const themeKind = defineKind({
  kind: 'theme', label: 'Mood', plural: 'Moods', icon: '🌄', verb: 'Choose',
  check(c) {
    const s = c.content.sky, out = [];
    if (s.fogFar <= s.fogNear + 100) out.push(warn('Very dense fog: you will hardly see anything'));
    if (s.exposure * s.sunIntensity < 0.5 && s.sunElevation < 5) out.push(warn('Very dark scene: the road may be hard to read'));
    if (Math.abs(lum(c.content.land.dirt) - lum(c.content.land.grass)) < 0.05) out.push(warn('Verges and grass of the same shade: the road is hard to tell apart'));
    return out;
  },
  facts: c => [
    { label: 'Sun', value: `${Math.round(c.content.sky.sunElevation)}°` },
    { label: 'Weather', value: c.content.weather },
    { label: 'Trees', value: c.content.decor.trees >= 1.4 ? 'forest' : c.content.decor.trees >= 0.6 ? 'hedgerows' : 'open' },
  ],
  ai: {
    summary: 'a visual mood for races (time of day, sky, season, landscape colours, weather)',
    effort: 'medium', maxTokens: 8000,
    brief: `A mood sets the 3D rendering of a race. All colours as "#rrggbb".
- sky: sunElevation (degrees, −4 to 85; 2 to 10 = sunset, 40+ = noon), sunAzimuth (0–360), haze (0 clear to 1 hazy), sun (sun colour), sky (sky), fog (fog at the horizon), cloud (clouds), fogNear and fogFar (m; 500/3600 clear, 150/1500 misty), exposure (0.4–0.6 usual), sunIntensity (2–4), ambient (0.5–1), clouds (0 bare sky to 2 overcast).
- land: grass, grassLight (two grass tones), dry (dry grass), dirt (verges), rock, sand (shores), snow, water, foliage (tree foliage), fields (1 to 9 field colours), snowLine (m above the lowest point where the snow starts, 20 = everywhere).
- decor: trees (0–2), conifers and poplars (shares of firs and poplars, 0–1), rocks, grass, flowers, crowd (spectators, 0–2).
- weather: clair, pluie, neige, brume or orage (ids kept as is).
Stay readable: the road (dark asphalt) must stand out from the verges.`,
    example: { kind: 'theme', name: 'Red desert', desc: 'Ochre canyons under a blazing sun.', tags: ['desert'], content: {
      sky: { sunElevation: 55, sunAzimuth: 140, haze: 0.2, sun: '#fff0d0', sky: '#bcd6f0', fog: '#e8c9a0', cloud: '#ffffff', fogNear: 400, fogFar: 3200, exposure: 0.48, sunIntensity: 3.8, ambient: 0.85, clouds: 0.3 },
      land: { grass: '#b5652e', grassLight: '#d08a4a', dry: '#c9a06a', dirt: '#8a4a28', rock: '#9a4a2a', sand: '#e0b880', snow: '#f1f4f8', water: '#2a6a7a', foliage: '#6b7a2e', fields: ['#c47a3e', '#b06030', '#d09a5a'], snowLine: 600 },
      decor: { trees: 0.15, conifers: 0, poplars: 0, rocks: 2, grass: 0.2, flowers: 0, crowd: 0.8 }, weather: 'clair',
    } } satisfies CreationOf<'theme'>,
  },
});
